// @flow
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import config from 'appconfig';

import { Checkbox } from '@atlaskit/checkbox';

import TotalPrice from 'modules/checkout/payment/components/TotalPrice';
import { Paragraph, SubHeader, RealignCheckbox } from 'modules/checkout/styled';
import { CheckoutModes } from 'modules/checkout/payment/duck/model/';
import type { PaymentPageProps } from 'modules/checkout/payment/duck/model';
import t from 'modules/i18n/intl';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGetCustomerByEmail } from 'modules/mac/duck/actions/contact'; // Adjust the path as needed new-cart/src/modules/mac/duck/actions/contact/actions.ts
import { selectGetCustomerByEmail } from 'modules//mac/duck/selectors/'; // Adjust the path as needed new-cart/src/modules/mac/duck/selectors/contact.ts
import { FeatureFlags, checkFeatureGate } from 'util/statsig/index';

const CreditRequestLink = (chunks, orgId) => (
  <a target="_blank" rel="noopener noreferrer" href={config.applicationFormLink(orgId)}>
    {chunks}
  </a>
);

const WeAlsoAcceptBankCheckqueOrAch = ({ currency }: PaymentPageProps) =>
  currency !== 'JPY' ? (
    <>
      {' '}
      <FormattedMessage id="payment.we-also-accept-bank-cheque-or-ach" />
    </>
  ) : null;

const BankTransferOnlyContent = (props: PaymentPageProps) => {
  const { currency } = props;

  return (
    <>
      <SubHeader>{t('payment.bank-transfer-or-check.content.title', { currency })}</SubHeader>
      <Paragraph>
        <FormattedMessage
          id="payment.bank-transfer-or-check.content.description"
          values={{ currency }}
        />
        <WeAlsoAcceptBankCheckqueOrAch currency={currency} />
      </Paragraph>
      <TotalPrice {...props} />
    </>
  );
};

const PayOnAccountTitle = () => <FormattedMessage id="payment.pay-on-account.content.title" />;
const PayOnAccountDescription = ({ currency }: PaymentPageProps) => (
  <Paragraph>
    <FormattedMessage id="payment.pay-on-account.content.description" />
    <WeAlsoAcceptBankCheckqueOrAch currency={currency} />
  </Paragraph>
);

const PayOnAccountOnlyContent = (props: PaymentPageProps) => (
  <>
    <SubHeader>
      <PayOnAccountTitle />
    </SubHeader>
    <PayOnAccountDescription />
    <TotalPrice {...props} />
  </>
);

const PayOnTermsTermsAndConditions = ({ cash }: PaymentPageProps) => (
  <>
    <br />
    <h4>{t('payment.net30.t&c')}</h4>
    <Paragraph>{t('payment.net30.advocate-review-pending')}</Paragraph>
    <Paragraph>{t('payment.net30.user-acceptance')}</Paragraph>
    <RealignCheckbox>
      <Checkbox
        label={t('payment.net30.t&c.checkbox.label')}
        isChecked={cash.payOnTermAgreed}
        onChange={(e) => cash.agreeToPayOnTerm(e.target.checked)}
      />
    </RealignCheckbox>
  </>
);

const PayOnTermsTitle = () => <FormattedMessage id="payment.net30.title" />;
const PayOnTermsDescription = ({ currency, showPayOnTermsFormText, orgId }: PaymentPageProps) => (
  <Paragraph>
    {showPayOnTermsFormText &&
      t(
        {
          id: 'payment.pay-on-terms.application.form.text',
          defaultMessage: 'Apply for Net 30 day terms using <link>application form</link>.',
        },
        {
          link: (chunks) => CreditRequestLink(chunks, orgId),
        },
      )}
    <FormattedMessage id="payment.pay-on-terms.content.description" />
    <WeAlsoAcceptBankCheckqueOrAch currency={currency} />
  </Paragraph>
);

const PayOnTermsOnlyContent = (props: PaymentPageProps) => (
  <>
    <SubHeader>
      <PayOnTermsTitle />
    </SubHeader>
    <PayOnTermsDescription {...props} />
    <TotalPrice {...props} />
    <PayOnTermsTermsAndConditions {...props} />
  </>
);

export type OptionalPaymentMethodProps = {
  title: any,
  targetCheckoutMode: any,
  actualCheckoutMode: any,
  label: any,
  children: any,
  selectMode: any,
  testId?: string,
};
const OptionalPaymentMethod = ({
  title,
  label,
  targetCheckoutMode,
  actualCheckoutMode,
  selectMode,
  children,
  testId,
}: OptionalPaymentMethodProps) => (
  <>
    <SubHeader>
      {title} (
      <em>
        <FormattedMessage id="payment.cash.optional" />
      </em>
      )
    </SubHeader>
    <RealignCheckbox>
      <Checkbox
        label={label}
        isChecked={targetCheckoutMode === actualCheckoutMode}
        onChange={(e: any) =>
          selectMode(e.target.checked ? targetCheckoutMode : CheckoutModes.BANK_TRANSFER)
        }
        testId={testId}
      />
    </RealignCheckbox>
    {actualCheckoutMode === targetCheckoutMode ? children : null}
  </>
);

const BankTransferWithMulitpleOptionsContent = (props: PaymentPageProps) => (
  <>
    <BankTransferOnlyContent {...props} />
    {props.cash.canPayOnAccount && (
      <OptionalPaymentMethod
        title={<PayOnAccountTitle />}
        label={<FormattedMessage id="payment.pay-on-account.checkbox.label" />}
        targetCheckoutMode={CheckoutModes.PAY_ON_ACCOUNT}
        actualCheckoutMode={props.checkoutMode}
        selectMode={props.selectMode}
      >
        <PayOnAccountDescription {...props} />
      </OptionalPaymentMethod>
    )}
    {props.cash.canPayOnTerm && (
      <OptionalPaymentMethod
        title={<PayOnTermsTitle />}
        label={
          <>
            {props.showPayOnTermsFormText ? (
              t(
                {
                  id: 'payment.pay-on-terms.application.form.text',
                  defaultMessage: 'Apply for Net 30 day terms using <link>application form</link>.',
                },
                {
                  link: (chunks) => CreditRequestLink(chunks, props.orgId),
                },
              )
            ) : (
              <FormattedMessage id="payment.net30.checkbox.label" />
            )}
            {!props.inSiteConfiguratorMode ? (
              <FormattedMessage id="payment.net30.checkbox.label-license-key-text" />
            ) : (
              ''
            )}
          </>
        }
        targetCheckoutMode={CheckoutModes.PAY_ON_TERMS}
        actualCheckoutMode={props.checkoutMode}
        selectMode={props.selectMode}
        testId="cashOnTermsCheckbox"
      >
        <PayOnTermsTermsAndConditions {...props} />
      </OptionalPaymentMethod>
    )}
  </>
);

const TabContent = (props: PaymentPageProps) => {
  const dispatch = useDispatch();
  const [orgId, setOrgId] = React.useState('');
  const { cash, checkoutMode, availablePaymentMethods, selectMode, currentContactEmail } = props;
  const showPayOnTermsFormText = checkFeatureGate(
    FeatureFlags.CART_SHOW_APPLICATION_FORM_TEXT_IN_PAY_ON_TERMS,
  );

  useEffect(() => {
    if (currentContactEmail && showPayOnTermsFormText) {
      dispatch(fetchGetCustomerByEmail(currentContactEmail));
    }
  }, [currentContactEmail]);

  const customersByEmail = useSelector(selectGetCustomerByEmail);

  useEffect(() => {
    if (customersByEmail[currentContactEmail]?.result && showPayOnTermsFormText) {
      const customerDetails = customersByEmail[currentContactEmail].result;
      setOrgId(customerDetails?.organisationDetails?.id);
    }
  }, [customersByEmail[currentContactEmail]]);

  if (!cash.isSelected) {
    return null;
  }

  if (cash.multipleOptionsAvailable) {
    return (
      <div>
        <BankTransferWithMulitpleOptionsContent
          {...props}
          orgId={orgId}
          showPayOnTermsFormText={showPayOnTermsFormText}
        />
      </div>
    );
  }

  if (!availablePaymentMethods.onTerms && checkoutMode === CheckoutModes.PAY_ON_TERMS) {
    selectMode(CheckoutModes.PAYPAL);
  }

  switch (checkoutMode) {
    case CheckoutModes.BANK_TRANSFER:
      return (
        <div>
          <BankTransferOnlyContent {...props} />
        </div>
      );
    case CheckoutModes.PAY_ON_ACCOUNT:
      return (
        <div>
          <PayOnAccountOnlyContent {...props} />
        </div>
      );
    case CheckoutModes.PAY_ON_TERMS:
      return (
        <div>
          <PayOnTermsOnlyContent
            {...props}
            showPayOnTermsFormText={showPayOnTermsFormText}
            orgId={orgId}
          />
        </div>
      );
    default:
      throw new Error(`Unexpected cash checkout mode ${checkoutMode}`);
  }
};

export default TabContent;
